import React, { useEffect, useState, useRef } from "react";
import { BackButton } from "../../common/GeneralComponent";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/partials/Loader";
import { useDispatch } from "react-redux";
import FeatureCategoryInfo from "./FeatureCategoryInfo";
import FeatureDesign from "./FeatureDesign";
import CriticalStages from "./CriticalStages";

const AddFeatureCategory = () => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [currentStepCompleted, setCurrentStepCompleted] = useState(false);

  const tabs = ["Feature Category", "Feature Design", "Critical Stages"];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });

      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };

  const handleCategoryCreated = (newCategoryId, data) => {
    setCategoryId(newCategoryId);
    setCategoryData(data);
    setCurrentStepCompleted(true);
    toast.success("Feature category created successfully!");
  };

  const handleDesignSaved = () => {
    setCurrentStepCompleted(true);
  };

  const handleStagesSaved = () => {
    navigate("/feature-category-view");
  };

  const handleNext = () => {
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
      setCurrentStepCompleted(false);
    }
  };

  const handleTabClick = (tab) => {
    const tabIndex = tabs.indexOf(tab);
    const currentIndex = tabs.indexOf(activeTab);

    if (
      tabIndex <= currentIndex ||
      (tabIndex === currentIndex + 1 && currentStepCompleted)
    ) {
      setActiveTab(tab);
      setCurrentStepCompleted(false);
    } else if (tabIndex > 0 && !categoryId) {
      toast.error("Please complete the Feature Category step first");
    }
  };

  const isTabDisabled = (tab) => {
    const tabIndex = tabs.indexOf(tab);
    if (tabIndex === 0) return false; // Feature Category is always enabled
    if (tabIndex === 1 && categoryId) return false; // Feature Design enabled after category created
    if (tabIndex === 2 && categoryId) return false; // Critical Stages enabled after category created
    return true;
  };

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Feature Category
        </h1>
        <BackButton text="Back" to="/feature-category-view" />
      </div>

      <div className="mt-6">
        <div className="flex items-center rounded-t-lg rounded-tr-lg bg-[#fdf5ec] relative">
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`px-10 py-2 font-semibold whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-[#caa316] text-white"
                    : isTabDisabled(tab)
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-black"
                }`}
                style={{
                  borderTopLeftRadius: "13px",
                  borderTopRightRadius: "30px",
                }}
                disabled={isTabDisabled(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>

        <div className="bg-white rounded-b-lg rounded-br-lg shadow-md p-6">
          {activeTab === "Feature Category" && (
            <FeatureCategoryInfo
              onCategoryCreated={handleCategoryCreated}
              onNext={handleNext}
              isEditMode={false}
              showSubmitAndNext={true}
              stepCompleted={currentStepCompleted}
            />
          )}

          {activeTab === "Feature Design" && categoryId && (
            <FeatureDesign
              featureCategoryId={categoryId}
              onDesignSaved={handleDesignSaved}
              onNext={handleNext}
              isEditMode={false}
              showSubmitAndNext={true}
              stepCompleted={currentStepCompleted}
            />
          )}

          {activeTab === "Critical Stages" && categoryId && (
            <CriticalStages
              featureCategoryId={categoryId}
              onStagesSaved={handleStagesSaved}
              isEditMode={false}
              showFinalSubmit={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFeatureCategory;
